import { Fragment } from 'react';

import {
  Button,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';

import { CellContext, ColumnDef, createColumnHelper, Row, Table } from '@tanstack/react-table';

import { Badge, MoreCircleOutline } from '@aslanpay/portal-common-lib';

import {
  PayCalendarActions,
  PayCalendarBulkActions,
  PayCalendarRow,
} from 'app/components/UI/Table/Table.types';

import {
  PayCalendarActionsIds,
  payCalendarActionsValues,
  PayCalendarField,
} from 'app/constants/table';
import { PayFrequency, PayType } from 'app/constants/payCalendar';
import { isPayCalendarArchived } from 'app/utils/payCalendars';

/**
 * The row actions that apply to a pay calendar: none while people are assigned or once it is
 * archived, otherwise Archive and Delete.
 */
export const getAvailablePayCalendarActions = (
  payCalendar: Pick<PayCalendarRow, 'employees' | 'status'>
): PayCalendarActionsIds[] => {
  if (payCalendar.employees > 0 || isPayCalendarArchived(payCalendar)) {
    return [];
  }
  return [PayCalendarActionsIds.ARCHIVE, PayCalendarActionsIds.DELETE];
};

export function getPayCalendarColumns(actions: PayCalendarActions) {
  const columnHelper = createColumnHelper<PayCalendarRow>();

  const fields = Object.keys(PayCalendarField) as Array<keyof PayCalendarRow>;

  // ✨ Explicitly tell TypeScript it's ColumnDef[], not just AccessorKeyColumnDef[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<PayCalendarRow, any>[] = fields.map((field) => {
    return columnHelper.accessor(field, {
      cell: (info) => {
        switch (field) {
          case 'name':
            return (
              <HStack spacing="8px">
                <Text as="h6" textStyle="h6SemiBold">
                  {info.getValue() as string}
                </Text>
                {isPayCalendarArchived(info.row.original) && (
                  <Badge status="disabled" value="Archived" />
                )}
              </HStack>
            );
          case 'payFrequency':
            return PayFrequency[info.getValue() as keyof typeof PayFrequency];
          case 'payType':
            return PayType[info.getValue() as keyof typeof PayType];
          case 'odpAllowancePercentage':
            return `${info.getValue()}%`;
          case 'startingBalanceDays':
            if (info.row.original.fullPeriodStartingBalance === true && info.getValue() === 0) {
              return 'Whole period';
            }
            return `${info.getValue()}`;
          case 'startDate':
            return info.getValue();
          default:
            return info.getValue();
        }
      },
      header: PayCalendarField[field as keyof typeof PayCalendarField],
      enableSorting: field !== 'employees',
      meta: {
        getCellContext: (context: CellContext<PayCalendarRow, unknown>) => ({
          onClick: () => actions.select(context.row.original),
        }),
      },
    });
  });
  columns.unshift({
    id: 'select',
    enableSorting: false,
    header: ({ table }: { table: Table<PayCalendarRow> }) => (
      <Checkbox
        {...{
          isChecked: table.getIsAllPageRowsSelected(),
          isIndeterminate: table.getIsSomePageRowsSelected(),
          onChange: table.getToggleAllPageRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }: { row: Row<PayCalendarRow> }) => (
      <Checkbox
        {...{
          isChecked: row.getIsSelected(),
          isIndeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    accessorKey: 'id',
    meta: {
      customStyles: { justifyContent: 'center' },
      pin: 'left',
      withBackground: true,
    },
  });

  columns.push(
    columnHelper.display({
      id: 'options',
      meta: {
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: ({ row }) => {
        const availableActions = getAvailablePayCalendarActions(row.original);

        if (!availableActions.length) {
          return null;
        }

        return (
          <Menu autoSelect={false} strategy="fixed">
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<MoreCircleOutline boxSize="20px" />}
              variant="menu"
            />
            <Portal>
              <MenuList>
                {availableActions.map((action, index) => {
                  const actionValue = payCalendarActionsValues.find((value) => value.id === action);
                  const isDelete = action === PayCalendarActionsIds.DELETE;

                  return (
                    <Fragment key={action}>
                      {index > 0 && <MenuDivider />}
                      <MenuItem
                        color={isDelete ? 'system.error' : 'brand.black'}
                        onClick={() => actions[action]([row.index])}
                        icon={
                          <Icon
                            as={actionValue?.icon}
                            boxSize="14px"
                            color={isDelete ? 'system.error' : 'brand.darkGray'}
                          />
                        }
                      >
                        {actionValue?.value}
                      </MenuItem>
                    </Fragment>
                  );
                })}
              </MenuList>
            </Portal>
          </Menu>
        );
      },
    })
  );

  return columns;
}

export function getPayCalendarBulkActionsValues<TData extends object>(
  actions: PayCalendarBulkActions,
  selectedRows: Row<TData>[]
) {
  const selectedPayCalendars = selectedRows.map((row) => row.original as PayCalendarRow);
  // Bulk Archive and Delete are both offered only when every selected calendar allows them.
  const isActionDisabled = selectedPayCalendars.some(
    (payCalendar) => !getAvailablePayCalendarActions(payCalendar).length
  );

  const bulkActions = {
    ...actions,
    archive: { ...actions.archive, isDisabled: isActionDisabled },
    delete: { ...actions.delete, isDisabled: isActionDisabled },
  };

  const bulkActionsElements = Object.keys(bulkActions).map((bulkAction) => {
    const actionValue = payCalendarActionsValues.find((value) => value.id === bulkAction);

    const variant = bulkAction === PayCalendarActionsIds.DELETE ? 'bulkDelete' : 'bulk';

    return (
      <Button
        key={bulkAction}
        size="bulk"
        variant={variant}
        onClick={() =>
          bulkActions[bulkAction as keyof PayCalendarBulkActions].action(
            selectedRows.map((row) => row.index)
          )
        }
        isDisabled={bulkActions[bulkAction as keyof PayCalendarBulkActions].isDisabled}
      >
        <Icon as={actionValue?.icon} boxSize="20px" />
        <Text>{actionValue?.value}</Text>
      </Button>
    );
  });

  return { label: 'pay calendar', bulkActions: bulkActionsElements };
}
